import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { buildParams, parseURL } from '../../../global/js/url';
import SearchContext from '../data/FunnelbackContext';
import SearchTabs from './_SearchTabs';
import SearchFilter from './_SearchFilter';
import styles from '../../css/functional.module.scss';

/**
 * Renders search facets and handles logic related to them.
 *
 * @param {boolean} renderTabs Flag to render Tabs.
 * @param {boolean} includeTabFacet Flag to include TAB type facets in filters.
 * @param {number}  exposedFacetsNumber The number of facets to display next to all filters
 * @param {number}  debounceDelay The number of milliseconds to wait before fetching
 * @param {bool} tabKeepFilters flag to determine if switching through tabs should remember filters
 * @param {bool} currentTabResults flag to determine if number of results should be displayed on current tab
 *
 * @returns {JSX.Element}
 * @constructor
 */
function SearchFacets({ renderTabs, includeTabFacet, exposedFacetsNumber, debounceDelay, tabKeepFilters, currentTabResults }) {
    const { isFetching, fetchResults, facets, queryString, setQueryString, activeTab } = useContext(SearchContext);
    const allWrapperRef = useRef(null);
    const [isAllFacetsVisible, setIsAllFacetsVisible] = useState(false);

    const handleClickOutsideAllFacets = (event) => {
        if (allWrapperRef.current && !allWrapperRef.current.contains(event.target)) {
            setIsAllFacetsVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutsideAllFacets, true);
        document.addEventListener('focus', handleClickOutsideAllFacets, true);
        return () => {
            document.removeEventListener('click', handleClickOutsideAllFacets, true);
            document.removeEventListener('focus', handleClickOutsideAllFacets, true);
        };
    });

    // Get only facets that have values and aren't a "TAB" Display Type facet
    const resultsFacets = facets.filter((facet) => {
        // Check if the facet has the tab value
        const isTab = facet.guessedDisplayType === 'TAB';
        // Check that the facet has results
        const hasResults = facet.allValues.length > 0;

        // If we want to include tabs
        if (includeTabFacet) {
            // Return all facets (including tab)
            return hasResults;
        }
        // Filter all facets that don't have results but also which are tabs
        return hasResults && !isTab;
    });

    // The clear button handler
    const onClearClickHandler = () => {
        const urlParams = parseURL(queryString.replace(/f\..*?&|f\..*?$/g, ''));
        const newParams = buildParams({ ...urlParams, ...activeTab }, true, true);

        setQueryString(newParams);
        fetchResults(null, newParams);
    };

    // Open the all filters wrapper
    const onShowAllClickHandler = () => {
        setIsAllFacetsVisible(true);
    };

    // Close the all filters wrapper
    const onCloseClickHandler = () => {
        setIsAllFacetsVisible(false);
    };

    // Get Tab wrapper to check if it should render loading screen
    const facetWrapper = document.getElementById('funnelback-instant-search-facets');

    // Render loading screen if isFetching flag is set to true and tab wrapper isn't exist
    if (isFetching && !facetWrapper) {
        return (
            <>
                <div className="sr-only">Loading...</div>
                <div className="funnelback-instant-search-facets--loading">
                    <div className="funnelback-instant-search-tabs--loading">
                        {[...Array(3)].map((item, index) => (
                            <div
                                className="funnelback-instant-search-tabs__button--loading"
                                // eslint-disable-next-line react/no-array-index-key
                                key={`result-${index}`}
                            />
                        ))}
                    </div>
                    <div className="funnelback-instant-search-filters--loading">
                        {[...Array(4)].map((item, index) => (
                            <div
                                className="funnelback-instant-search-filters__button--loading"
                                // eslint-disable-next-line react/no-array-index-key
                                key={`result-${index}`}
                            />
                        ))}
                    </div>
                </div>
            </>
        );
    }

    // Return nothing if facets are empty
    if (!facets || facets.length === 0) {
        return '';
    }

    return (
        <div id="funnelback-instant-search-facets" className={`funnelback-instant-search-facets ${styles.facet} no-wysiwyg`}>
            {renderTabs && <SearchTabs tabKeepFilters={tabKeepFilters} currentTabResults={currentTabResults} />}
            <div className="funnelback-instant-search-facets__filters">
                <SearchFilter facetData={resultsFacets} limit={exposedFacetsNumber} debounceDelay={debounceDelay} />
                <button
                    id="funnelback-instant-search-facets-group-all"
                    className={`funnelback-instant-search-facets__group-all ${styles.facetGroup} ${styles.facetGroupAll} ${
                        isAllFacetsVisible ? 'active' : ''
                    }`}
                    type="button"
                    aria-expanded={isAllFacetsVisible}
                    aria-controls="funnelback-instant-search-facets-group-list-all"
                    tabIndex={0}
                    onClick={onShowAllClickHandler}
                >
                    Show all filters
                    <svg className={`funnelback-instant-search-facets__icon ${styles.facetIcon}`}>
                        <use href="#filter" />
                    </svg>
                </button>
                {isAllFacetsVisible && (
                    <div
                        ref={allWrapperRef}
                        id="funnelback-instant-search-facets-group-list-all"
                        role="region"
                        aria-labelledby="funnelback-instant-search-facets-group-all"
                        className={`funnelback-instant-search-facets__list funnelback-instant-search-facets__list--all ${styles.facetList} ${styles.facetListAll}`}
                    >
                        <div className={`funnelback-instant-search-facets__heading ${styles.facetHeading}`}>
                            <h2 className={`funnelback-instant-search-facets__header ${styles.facetHeader}`}>ALL FILTERS</h2>
                            <button
                                className={`funnelback-instant-search-facets__close ${styles.facetClose}`}
                                type="button"
                                tabIndex={0}
                                onClick={onCloseClickHandler}
                            >
                                <svg className={`funnelback-instant-search-facets__icon ${styles.facetIcon}`}>
                                    <use href="#close" />
                                </svg>
                                <span className="sr-only">Close filter pannel</span>
                            </button>
                            <button
                                className={`funnelback-instant-search-facets__clear  ${styles.facetClear}`}
                                type="button"
                                tabIndex={0}
                                onClick={onClearClickHandler}
                            >
                                <svg className={`funnelback-instant-search-facets__icon ${styles.facetIcon}`}>
                                    <use href="#trash" />
                                </svg>
                                Clear all filters
                            </button>
                        </div>
                        <div className={`funnelback-instant-search-facets__filters ${styles.facetFilters}`}>
                            <SearchFilter facetData={resultsFacets} allFacets debounceDelay={debounceDelay} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

SearchFacets.propTypes = {
    renderTabs: PropTypes.bool,
    includeTabFacet: PropTypes.bool,
    exposedFacetsNumber: PropTypes.number,
    debounceDelay: PropTypes.number,
    tabKeepFilters: PropTypes.bool,
    currentTabResults: PropTypes.bool,
};

SearchFacets.defaultProps = {
    renderTabs: true,
    includeTabFacet: false,
    exposedFacetsNumber: 3,
    debounceDelay: 500,
    tabKeepFilters: false,
    currentTabResults: false,
};

export default SearchFacets;
